/**
 * Edit component for managing lease record modifications.
 *
 * This component provides a form-based interface for editing lease records,
 * including general lease information, financial terms, accounting information,
 * and IFRS 16-specific fields. It integrates with APIs to fetch, update, and
 * populate lease data, and provides features like schedule population and
 * total lease view.
 *
 * @component
 *
 * @requires react
 * @requires react-redux
 * @requires react-router-dom
 * @requires lodash
 * @requires dayjs
 * @requires dayjs/plugin/utc
 * @requires antd
 * @requires @mui/material
 * @requires react-number-format
 * @requires react-toastify
 * @requires store/features/api/apiSlice
 * @requires store/features/process/processApi
 * @requires store/features/api/graphSlice
 * @requires components/dev/AntdValuePreview
 * @requires components/header/DetailPageHeader
 * @requires components/InputLabel
 * @requires components/MainCard
 * @requires config
 * @requires utils/utils
 * @requires pages/comptroller/ComptrollerConfirmModal
 * @requires assets/images/solution_white.svg
 *
 * @returns {JSX.Element} The Edit component.
 *
 * @example
 * <Edit />
 *
 * @description
 * - Fetches and displays lease data for editing.
 * - Handles form submission for updating lease records.
 * - Provides functionality to add rows, populate schedules, and view total lease data.
 * - Integrates with Redux for state management and APIs for data fetching/updating.
 * - Uses Ant Design and Material-UI for UI components.
 *
 * @function handleShowAll
 * Fetches and displays the total lease view data.
 *
 * @function handleSubmit
 * Prepares and submits the form data for updating the lease record.
 *
 * @function handleLeaseDateChange
 * Automatically calculates and updates the lease end date based on the start date and term.
 *
 * @function handlePreview
 * Prepares and sends a payload to populate the lease schedule.
 *
 * @function initialDataLoad
 * Loads initial data for inactive leases and populates the lease control data.
 *
 * @function handleAddRow
 * Adds a new row to the form for lease modifications.
 *
 * @function utcConverter
 * Converts a Day.js date object to UTC format.
 *
 * @function handleInputMonthChange
 * Calculates and updates the month difference for a lease change based on the effective date.
 *
 * @function queryV3Loader
 * Fetches lease schedule data for active leases based on the selected version.
 *
 * @useEffect
 * - Loads initial lease data on component mount.
 * - Handles updates to lease data and navigation on success or error.
 * - Ensures the process code is valid and fetches single identity data.
 */

import { Col, Form, Input, message, Row, Spin } from 'antd';
import solution from 'assets/images/solution_white.svg';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import DetailPageHeader from 'components/header/DetailPageHeader';
import InputLabel from 'components/InputLabel/index';
import { toastConfig } from 'config';
import { debounce, isEmpty } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import LeaseTable from './leaseRecordTable';
import dayjs from 'dayjs';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import CardAccordion from './CustomComponents/CardAccordion';
import { Box } from '@mui/material';
import { encodeQueryParamData } from 'utils/utils';
import utc from 'dayjs/plugin/utc';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import cancelIcon from 'assets/images/icons/deactivated.svg';
import { constantFormValues } from './root';
import { useControlData } from './Create';
import GeneralLeaseInformation from './CustomComponents/cardSections/GeneralLeaseInformation';
import FinancialTermsConditions from './CustomComponents/cardSections/FinancialTermsConditions';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import IFRS16FieldsModifications from './CustomComponents/cardSections/IFRS16FieldsModifications';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';

export const assurerAndControlOwnerOptions = [
	{ key: 1, label: 'Position', value: 'DATA_positionList' },
	{ key: 2, label: 'Process Role', value: 'DATA_processRoleList' },
	{ key: 3, label: 'PGR', value: 'DATA_PGRList' },
];

dayjs.extend(utc);

const Edit = () => {
	const [form] = Form.useForm();
	const [isLoadDataButtonDisabled, setIsLoadDataButtonDisabled] = useState(true);
	const [flag, setFlag] = useState(false);
	const [isActionRequiredButtonClicked, setIsActionRequiredButtonClicked] = useState(false);
	const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(true);
	const [newRowAdded, setNewRowAdded] = useState(false);
	const { state } = useLocation();
	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	const navigate = useNavigate();
	const [isPopulateClick, setIsPopulateClick] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [submitData, setSubmitData] = useState(null);
	const [leaseControlData, setLeaseControlData] = useState([]);
	const [showAllDataList, setShowAllDataList] = useState([]);
	const changes = Form.useWatch('changes', form);
	const [rowIndex, setRowIndex] = useState(0);
	const [modalData, setModalData] = useState({
		head: state?.processName ?? '',
		label: 'Do you want to proceed?',
	});
	const [yearLiabilityData, setYearLiabilityData] = useState({});
	// const [effectiveDateCheckerData, setEffectiveDateCheckerData] = useState([]);
	// const [initialFormValuesTrack, setInitialFormValuesTrack] = useState(null);
	const [initialFormValues, setInitialFormValues] = useState({});
	const [leaseHasChange, setLeaseHasChange] = useState(false);
	const [pdfFileList, setPdfFileList] = useState([]);

	const [viewSingleIdentity, { data: viewData, isLoading: isViewLoading, isSuccess: isViewSuccess, isError: isViewError }] =
		useGetSingleIdentityMutation();
	const statusData = Object.keys(viewData?.parkedData ?? {})?.length === 0 ? viewData?.data : viewData?.parkedData;
	const [UpdateSingleIdentity, { error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] = useGetSingleIdentityMutation();

	const { lynFormProperties } = useControlData(state?.processCode);

	const [sendControlData, { isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
	const [getLeaseTableData, { isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();

	const sendControlDataWithPayload = useCallback(
		async (payloadData, scriptName = 'lease') => {
			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);

			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName,
				payloadData,
			};

			return sendControlData(formattedPayload);
		},
		[sendControlData, state?.processCode]
	);

	const handleLeaseControllerClick = async () => {
		await sendControlDataWithPayload({ leaseIdentityId: viewData?._id }, 'lease-controller-lease-year-rou').then((response) => {
			setYearLiabilityData(response?.data?.responseData);
		});
	};

	const handleShowAll = useCallback(async () => {
		const payloadData = {
			leaseId: statusData?.leaseId,
		};

		try {
			const response = await sendControlDataWithPayload(payloadData, 'lease-controller-schedule-get-findata');
			setShowAllDataList(response?.data);
		} catch (error) {
			console.error('Error fetching lease view data:', error);
		}
	}, [statusData?.leaseId, sendControlDataWithPayload]);

	const handleSubmit = useCallback(
		(data) => {
			setModalData({ head: 'Confirmation', label: `Do you want to update?` });

			let { changes, ...rest } = data;

			const modifiedChanges = changes.map((change) => {
				const { inputChangeEffectiveDate, ...rest } = change;
				return { ...rest, inputChangeEffectiveDate: utcConverter(inputChangeEffectiveDate) };
			});
			const paymentModalityLabelValue = form.getFieldValue('paymentModality') === 'start' ? 'Advance basis' : 'Acrual Basis';

			const payloadData = { ...rest, changes: modifiedChanges, paymentModalityLabel: paymentModalityLabelValue };
			setSubmitData(payloadData);
			setShowModal(true);
		},
		[form]
	);

	const utcConverter = useCallback((dayjsDate) => {
		if (!dayjsDate) return null;

		const dateStr = dayjsDate.format('YYYY-MM-DD');
		return dayjs.utc(dateStr, 'YYYY-MM-DD');
	}, []);

	const handleLeaseDateChange = useCallback(() => {
		const leaseStartDate = form.getFieldValue('leaseStartDate');
		const leaseTerm = form.getFieldValue('leaseTerm');

		if (leaseStartDate && leaseTerm) {
			const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day');

			form.setFieldsValue({
				leaseEndDate,
			});
		}
	}, [form]);

	const debouncedHandleLeaseDateChange = useMemo(() => debounce(handleLeaseDateChange, 300), [handleLeaseDateChange]);

	const handleUpdateButtonActions = useCallback(() => {
		if (isLoadDataButtonDisabled && isUpdateButtonDisabled) {
			message.error({
				content: 'No new modifications were made',
				icon: <img src={cancelIcon} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		} else if (!isLoadDataButtonDisabled && isUpdateButtonDisabled) {
			message.error({
				content: 'Please click on the Load New Data to load',
				icon: <img src={cancelIcon} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
			});
		} else if (isLoadDataButtonDisabled && !isUpdateButtonDisabled) {
			form.submit();
		} else {
			return;
		}
	}, [form, isLoadDataButtonDisabled, isUpdateButtonDisabled]);

	const handlePreview = useCallback(async () => {
		setLeaseControlData([]);

		let payloadData = {};
		const effectiveDate = dayjs(initialFormValues?.changes[0].inputChangeEffectiveDate) ?? dayjs();

		const startDay = dayjs(effectiveDate).date();
		const startMonth = dayjs(effectiveDate).month() + 1;
		const startYear = dayjs(effectiveDate).year();
		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));

		const formChanges = form.getFieldValue('changes') ?? [];

		const processedChanges = formChanges.slice(1).map((change) => {
			let monthDifference = 0;

			if (change.inputChangeEffectiveDate) {
				const effectiveDate = dayjs(change.inputChangeEffectiveDate);
				monthDifference = (effectiveDate.year() - leaseStartDate.year()) * 12 + (effectiveDate.month() - leaseStartDate.month()) + 1;

				if (monthDifference < 0) {
					monthDifference = 0;
				}

				change.inputChangeMonth = monthDifference;
				change.inputChangeEffectiveDate = utcConverter(change.inputChangeEffectiveDate);
			}

			return change;
		});

		payloadData = {
			effectiveDate: utcConverter(dayjs(initialFormValues?.changes[0].inputChangeEffectiveDate)),
			advanceAmount: initialFormValues?.advanceAmount,
			grossRent: statusData?.changes[0]?.inputChangeGrossAmount,
			leaseTerm: statusData?.changes[0]?.inputChangeLeaseTerm,
			discountRate: statusData?.changes[0]?.inputChangeDiscountRate,
			startYear: String(startYear),
			startMonth: String(startMonth),
			startDay: String(startDay),
			escalation: statusData?.changes[0]?.inputChangeEscalation,
			paymentFrequency: statusData?.changes[0]?.paymentFrequency,
			paymentTime: statusData?.changes[0]?.paymentTime,
			advanceDuration: statusData?.advanceDuration,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			securityDeposit: statusData?.securityDeposit,
			initialDirectCost: statusData?.initialDirectCost,
			endOfFinancialYear: utcConverter(dayjs(statusData?.endOfFinancialYear)),
			changes: processedChanges,
		};

		try {
			const response = await sendControlDataWithPayload(payloadData);
			setLeaseControlData(response?.data?.responseData);
			setIsPopulateClick(true);
		} catch (error) {
			console.error('Error in preview:', error);
		}
	}, [initialFormValues, statusData, form, sendControlDataWithPayload, utcConverter]);

	const initialDataLoad = useCallback(async () => {
		handleLeaseControllerClick();
		if (viewData?.activationStatus?.toLowerCase() !== 'active') {
			let payloadData = {};

			payloadData = {
				advanceAmount: statusData?.advanceAmount,
				grossRent: statusData?.changes[0]?.inputChangeGrossAmount,
				leaseTerm: statusData?.changes[0]?.inputChangeLeaseTerm,
				discountRate: statusData?.changes[0]?.inputChangeDiscountRate,
				startYear: String(dayjs(statusData?.leaseStartDate).$y),
				escalation: statusData?.changes[0]?.inputChangeEscalation,
				startMonth: String(dayjs(statusData?.leaseStartDate).month() + 1),
				paymentFrequency: statusData?.changes[0]?.paymentFrequency,
				paymentTime: statusData?.changes[0]?.paymentTime,
				advanceDuration: statusData?.advanceDuration,
				startDay: String(dayjs(statusData?.leaseStartDate).date()),
				effectiveDate: utcConverter(dayjs(statusData?.changes[0].inputChangeEffectiveDate)),
				upfrontLeasePayment: statusData?.upfrontLeasePayment,
				securityDeposit: statusData?.securityDeposit,
				initialDirectCost: statusData?.initialDirectCost,
				endOfFinancialYear: utcConverter(dayjs(statusData?.endOfFinancialYear)),
				changes: statusData?.changes?.slice(1),
			};
			if (payloadData['advanceAmount'] === null || payloadData['advanceAmount'] === undefined) return;

			try {
				const response = await sendControlDataWithPayload(payloadData);
				setLeaseControlData(response?.data?.responseData);
			} catch (error) {
				console.error('Error loading initial data:', error);
			}
		}
	}, [statusData, viewData, sendControlDataWithPayload, utcConverter]);

	const isGeneralFieldsChanged = useCallback(() => {
		const currentValues = form.getFieldsValue();
		return (
			currentValues.leaseTitle !== initialFormValues.leaseTitle ||
			currentValues.leaseType !== initialFormValues.leaseType ||
			currentValues.lessorName !== initialFormValues.lessorName ||
			currentValues.rightOfUseAssetValue !== initialFormValues.rightOfUseAssetValue ||
			currentValues.glleaseLiability !== initialFormValues.glleaseLiability ||
			currentValues.costCenterInterestExpense !== initialFormValues.costCenterInterestExpense ||
			currentValues.glInterestExpense !== initialFormValues.glInterestExpense ||
			currentValues.leaseExpenseCostCenter !== initialFormValues.leaseExpenseCostCenter ||
			currentValues.leaseExpense !== initialFormValues.leaseExpense ||
			currentValues.leasePayableControlAccount !== initialFormValues.leasePayableControlAccount ||
			currentValues.glAdvanceRent !== initialFormValues.glAdvanceRent ||
			currentValues.depreciationRoUCostCenter !== initialFormValues.depreciationRoUCostCenter ||
			currentValues.depreciationRoU !== initialFormValues.depreciationRoU ||
			currentValues.accumDepreciationRoU !== initialFormValues.accumDepreciationRoU ||
			currentValues.glBankAccount !== initialFormValues.glBankAccount ||
			currentValues.advanceAmount !== initialFormValues.advanceAmount ||
			currentValues.advanceDuration !== initialFormValues.advanceDuration ||
			currentValues.securityDeposit !== initialFormValues.securityDeposit ||
			currentValues.initialDirectCost !== initialFormValues.initialDirectCost ||
			currentValues.endOfFinancialYear !== initialFormValues.endOfFinancialYear ||
			currentValues.upfrontLeasePayment !== initialFormValues.upfrontLeasePayment ||
			currentValues.securityDepositHead !== initialFormValues.securityDepositHead ||
			currentValues.accountsPayableHead !== initialFormValues.accountsPayableHead
		);
	}, [form, initialFormValues]);

	const isRequiredFieldsChanged = useCallback(() => {
		const currentValues = form.getFieldsValue();
		return (
			currentValues.leaseTerm !== initialFormValues.leaseTerm ||
			currentValues.paymentFrequency !== initialFormValues.paymentFrequency ||
			currentValues.paymentModality !== initialFormValues.paymentModality ||
			currentValues.grossRent !== initialFormValues.grossRent ||
			currentValues.escalationPercentage !== initialFormValues.escalationPercentage ||
			currentValues.initialDiscountRate !== initialFormValues.initialDiscountRate
		);
	}, [form, initialFormValues]);

	const isLoadNewDataButtonDisabled = useCallback(() => {
		const changeList = form.getFieldsValue('changes')?.changes;
		if (!changeList || changeList.length === 0) return true;

		const lastChange = changeList[changeList.length - 1];
		return (
			lastChange.inputChangeLeaseTerm === form.getFieldValue('leaseTerm') &&
			lastChange.paymentFrequency === form.getFieldValue('paymentFrequency') &&
			lastChange.paymentTime === form.getFieldValue('paymentModality') &&
			lastChange.inputChangeGrossAmount === form.getFieldValue('grossRent') &&
			lastChange.inputChangeEscalation === form.getFieldValue('escalationPercentage') &&
			lastChange.inputChangeDiscountRate === form.getFieldValue('initialDiscountRate')
		);
	}, [form]);

	const updateButtonDisablilityCheck = useCallback(() => {
		const effectiveDate = form.getFieldValue(['changes', initialFormValues?.changes?.length, 'inputChangeEffectiveDate']);
		const generalChanged = isGeneralFieldsChanged();
		const requiredChanged = isRequiredFieldsChanged();
		const loadDataDisabled = isLoadNewDataButtonDisabled();

		if (generalChanged) {
			if (requiredChanged) {
				if (!loadDataDisabled) {
					return true;
				} else {
					return effectiveDate ? false : true;
				}
			} else {
				return false;
			}
		} else {
			if (requiredChanged) {
				if (!loadDataDisabled) {
					return true;
				} else {
					return effectiveDate ? false : true;
				}
			} else {
				return true;
			}
		}
	}, [form, initialFormValues?.changes?.length, isGeneralFieldsChanged, isRequiredFieldsChanged, isLoadNewDataButtonDisabled]);

	const handleAddRow = useCallback(async () => {
		// Get the current values from the form fields
		const currentValues = {
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentTime: form.getFieldValue('paymentModality'),
			inputChangeLeaseTerm: form.getFieldValue('leaseTerm'),
			inputChangeGrossAmount: form.getFieldValue('grossRent'),
			inputChangeDiscountRate: form.getFieldValue('initialDiscountRate'),
			inputChangeEscalation: form.getFieldValue('escalationPercentage'),
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAmortizationPeriod: form.getFieldValue('advanceDuration'),
		};

		// Get the current list of changes
		const currentList = form.getFieldValue('changes') || [];

		// Get reference values from the status data
		const referenceValues = {
			paymentFrequency: statusData?.changes?.[rowIndex - 1]?.paymentFrequency,
			paymentTime: statusData?.changes?.[rowIndex - 1]?.paymentTime,
			inputChangeLeaseTerm: statusData?.changes?.[rowIndex - 1]?.inputChangeLeaseTerm,
			inputChangeGrossAmount: statusData?.changes?.[rowIndex - 1]?.inputChangeGrossAmount,
			inputChangeDiscountRate: statusData?.changes?.[rowIndex - 1]?.inputChangeDiscountRate,
			inputChangeEscalation: statusData?.changes?.[rowIndex - 1]?.inputChangeEscalation,
		};

		const formattedCurrentValues = {
			paymentFrequency: String(currentValues.paymentFrequency ?? ''),
			paymentTime: String(currentValues.paymentTime ?? ''),
			inputChangeLeaseTerm: String(currentValues.inputChangeLeaseTerm ?? ''),
			inputChangeGrossAmount: String(currentValues.inputChangeGrossAmount ?? ''),
			inputChangeDiscountRate: String(currentValues.inputChangeDiscountRate ?? ''),
			inputChangeEscalation: String(currentValues.inputChangeEscalation ?? ''),
		};

		const formattedReferenceValues = {
			paymentFrequency: String(referenceValues.paymentFrequency ?? ''),
			paymentTime: String(referenceValues.paymentTime ?? ''),
			inputChangeLeaseTerm: String(referenceValues.inputChangeLeaseTerm ?? ''),
			inputChangeGrossAmount: String(referenceValues.inputChangeGrossAmount ?? ''),
			inputChangeDiscountRate: String(referenceValues.inputChangeDiscountRate ?? ''),
			inputChangeEscalation: String(referenceValues.inputChangeEscalation ?? ''),
		};

		const hasChanged = Object.keys(formattedCurrentValues).some((key) => formattedCurrentValues[key] !== formattedReferenceValues[key]);

		// Get the current length of changes
		const changeLength = currentList.length;

		// If we already have a change beyond rowIndex
		if (changeLength > rowIndex) {
			// Get the existing row
			const existingRow = currentList[changeLength - 1];

			// For an existing row, preserve the effective date if it's been set by the user
			if (existingRow?.inputChangeEffectiveDate) {
				currentValues.inputChangeEffectiveDate = existingRow.inputChangeEffectiveDate;
			}

			if (existingRow?.inputChangeMonth) {
				currentValues.inputChangeMonth = existingRow.inputChangeMonth;
			}

			// Update the row
			const newList = [...currentList];
			newList[changeLength - 1] = currentValues;

			// Update form if values have changed
			if (hasChanged) {
				form.setFieldsValue({ changes: newList });
				setNewRowAdded(true);
			}
		} else {
			// For a brand new row, don't set inputChangeEffectiveDate
			const newList = [...currentList, currentValues];

			if (hasChanged) {
				form.setFieldsValue({ changes: newList });
				setNewRowAdded(true);

				requestAnimationFrame(() => {
					const newRowIndex = newList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});
			}
		}
		const formChanges = form.getFieldsValue().changes;

		// Check if the last two rows are identical (if we have at least 2 rows)
		if (formChanges?.length >= 2) {
			const lastRow = {
				inputChangeDiscountRate: form.getFieldValue('initialDiscountRate'),
				inputChangeEscalation: form.getFieldValue('escalationPercentage'),
				inputChangeGrossAmount: form.getFieldValue('grossRent'),
				inputChangeLeaseTerm: form.getFieldValue('leaseTerm'),
				paymentFrequency: form.getFieldValue('paymentFrequency'),
				paymentTime: form.getFieldValue('paymentModality'),
			};
			const secondLastRow = formChanges[formChanges.length - 2];

			const keysToCompare = [
				'paymentFrequency',
				'paymentTime',
				'inputChangeLeaseTerm',
				'inputChangeGrossAmount',
				'inputChangeDiscountRate',
				'inputChangeEscalation',
			];

			const areRowsIdentical = keysToCompare.every((key) => {
				return String(lastRow?.[key] || '') === String(secondLastRow?.[key] || '');
			});

			// If identical, remove the last row
			if (areRowsIdentical) {
				const trimmedList = [...currentList];
				trimmedList.pop();
				form.setFieldsValue({ changes: trimmedList });
				setNewRowAdded(false);
			}
		}

		// Update button states based on form changes
		setIsLoadDataButtonDisabled(true);
		const UpdateCheck = updateButtonDisablilityCheck();
		const LoadCheck = isLoadNewDataButtonDisabled();

		setIsUpdateButtonDisabled(UpdateCheck);
		setIsLoadDataButtonDisabled(LoadCheck);
	}, [rowIndex, statusData?.changes?.length]);

	const handleInputMonthChange = useCallback(
		(e, index) => {
			// Return early if date is null or undefined
			if (!e) {
				return;
			}

			const effectiveDate = dayjs(e);
			// Use the lease start date as the reference point
			const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));

			// Calculate month difference from lease start date
			let monthDifference = (effectiveDate.year() - leaseStartDate.year()) * 12 + (effectiveDate.month() - leaseStartDate.month()) + 1;

			// Ensure non-negative
			if (monthDifference < 0) {
				monthDifference = 0;
			}

			// Get current dates
			const currentEffectiveDate = effectiveDate;
			const effectiveDateMonth = currentEffectiveDate.month();
			const effectiveDateYear = currentEffectiveDate.year();
			const currentStartDate = dayjs(form.getFieldValue('leaseStartDate')).date();

			// Calculate the modified effective date
			let modifiedEffectiveDate = dayjs(`${effectiveDateYear}-${effectiveDateMonth + 1}-${currentStartDate}`);

			// Handle month end adjustments
			if (effectiveDateMonth !== modifiedEffectiveDate.month()) {
				modifiedEffectiveDate = modifiedEffectiveDate.subtract(1, 'day');
				if (effectiveDateMonth === 1) {
					// February special case
					if (effectiveDateYear % 4 === 0) {
						// Leap year
						modifiedEffectiveDate = modifiedEffectiveDate.subtract(2, 'day');
					} else {
						modifiedEffectiveDate = modifiedEffectiveDate.subtract(3, 'day');
					}
				}
			}

			// Update the specific row in the form with both the date and month values
			const currentChanges = form.getFieldValue('changes');
			if (currentChanges && currentChanges[index]) {
				// Set both values in the form
				currentChanges[index].inputChangeEffectiveDate = modifiedEffectiveDate;
				currentChanges[index].inputChangeMonth = monthDifference;

				// Update the form with both values
				form.setFieldsValue({ changes: currentChanges });
			}
		},
		[form]
	);

	const queryV3Loader = useCallback(async () => {
		if (
			+form.getFieldValue('version') &&
			companyCode &&
			(viewData?.activationStatus?.toLowerCase() === 'active' ||
				viewData?.activationStatus?.toLowerCase() === 'closed' ||
				viewData?.activationStatus?.toLowerCase() === 're-open')
		) {
			try {
				const res = await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, form.getFieldValue('version')],
					name: 'leaseSchedulesOnVersion',
				});

				setLeaseControlData(res?.data?.results);
				// setEffectiveDateCheckerData(res?.data?.results);
				return res;
			} catch (error) {
				console.error('Error loading query data:', error);
			}
		}
		return null;
	}, [companyCode, form, getLeaseTableData, state._id, viewData?.activationStatus]);

	useEffect(() => {
		if (statusData) {
			const data = statusData;
			const viewValues = {
				leaseId: data?.leaseId ?? '',
				leaseType: data?.leaseType ?? '',
				leaseTitle: data?.leaseTitle ?? '',
				lessorName: data?.lessorName ?? '',
				leaseCategory: data?.leaseCategory ?? '',
				leaseName: data?.leaseName ?? '',
				contactDetails: data?.contactDetails ?? '',
				leaseTerm: data?.leaseTerm ?? '',
				paymentModality: data?.paymentModality ?? '',
				leaseStartDate: dayjs(data?.leaseStartDate) ?? '',
				leaseEndDate: dayjs(data?.leaseEndDate) ?? '',
				renewalOptions: data?.renewalOptions ?? '',
				terminationOption: data?.terminationOption ?? '',
				advanceAmount: data?.advanceAmount ?? '',
				month: data?.month ?? '',
				advanceDuration: data?.advanceDuration ?? '',
				refundability: data?.refundability ?? '',
				endOfFinancialYear: dayjs(data?.endOfFinancialYear) ?? '',
				securityDeposit: data?.securityDeposit ?? '',
				initialDirectCost: data?.initialDirectCost ?? '',
				upfrontLeasePayment: data?.upfrontLeasePayment ?? '',
				responsibility: data?.responsibility ?? '',
				grossRent: data?.grossRent ?? '',
				advanceAmortization: data?.advanceAmortization ?? '',
				paymentFrequency: data?.paymentFrequency ?? '',
				paymentDueDate: data?.paymentDueDate ?? '',
				escalationPercentage: data?.escalationPercentage ?? '',
				escalationFrequency: data?.escalationFrequency ?? '',
				initialDiscountRate: data?.initialDiscountRate ?? '',
				basisOfRate: data?.basisOfRate ?? '',
				nonLeaseComponents: data?.nonLeaseComponents ?? '',
				separateServiceCharges: data?.separateServiceCharges ?? '',
				modificationDetails: data?.modificationDetails ?? '',
				discountRateChanges: data?.discountRateChanges ?? '',
				rightOfUseAssetValue: data?.rightOfUseAssetValue ?? '',
				leaseLiability: data?.leaseLiability ?? '',
				amortizationSchedule: data?.amortizationSchedule ?? '',
				version: String(data?.version) ?? '',
				glleaseLiability: data?.glleaseLiability ?? '',
				costCenterInterestExpense: data?.costCenterInterestExpense ?? '',
				glInterestExpense: data?.glInterestExpense ?? '',
				leasePayableControlAccount: data?.leasePayableControlAccount ?? '',
				glAdvanceRent: data?.glAdvanceRent ?? '',
				depreciationRoUCostCenter: data?.depreciationRoUCostCenter ?? '',
				depreciationRoU: data?.depreciationRoU ?? '',
				accumDepreciationRoU: data?.accumDepreciationRoU ?? '',
				glBankAccount: data?.glBankAccount ?? '',
				creationDate: data?.creationDateTime?.localDateTimeStringAMPM ? dayjs(data?.creationDateTime?.localDateTimeStringAMPM) : null,
				lastModifiedDate: data?.modifiedDateTime?.localDateTimeStringAMPM ? dayjs(data?.modifiedDateTime?.localDateTimeStringAMPM) : null,
				leaseExpense: data?.leaseExpense ?? '',
				leaseExpenseCostCenter: data?.leaseExpenseCostCenter ?? '',
				accountsPayableHead: data?.accountsPayableHead ?? '',
				securityDepositHead: data?.securityDepositHead ?? '',
				agreementFiles: data?.agreementFiles,
				changes: data?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: utcConverter(dayjs(inputChangeEffectiveDate)),
					};
				}),
			};
			// setInitialFormValuesTrack({
			// 	paymentFrequency: data?.paymentFrequency ?? '',
			// 	paymentTime: data?.paymentModality ?? '',
			// 	inputChangeLeaseTerm: data?.leaseTerm ?? '',
			// 	inputChangeGrossAmount: data?.grossRent ?? '',
			// 	inputChangeDiscountRate: data?.initialDiscountRate ?? '',
			// 	inputChangeEscalation: data?.escalationPercentage ?? '',
			// 	inputChangeAdvance: data?.advanceAmount ?? '',
			// 	inputChangeAmortizationPeriod: data?.advanceDuration ?? '',
			// });
			setRowIndex(statusData?.changes?.length);

			// console.log('agreementFiles', data?.agreementFiles);

			if (data?.agreementFiles?.length > 0) {
				const files = data?.agreementFiles?.map((item) => (item.file ? JSON.parse(item.file) : []));
				setPdfFileList(files);
			}

			form.setFieldsValue(viewValues);
			setInitialFormValues(viewValues);
		}
	}, [statusData, form, utcConverter, isViewSuccess, isViewError]);

	useEffect(() => {
		if (!isPopulateClick && statusData) {
			initialDataLoad();
		}
	}, [isPopulateClick, statusData, initialDataLoad]);

	useEffect(() => {
		if (!changes || changes.length < 2) {
			return;
		}

		const lastIndex = changes.length - 1;
		const secondLastIndex = changes.length - 2;

		// Extract key fields excluding date-related fields
		const lastIndexedValue = changes[lastIndex] || {};
		const secondLastIndexedValue = changes[secondLastIndex] || {};

		const normalizeObject = (obj) => {
			const result = {};
			Object.keys(obj).forEach((key) => {
				result[key] = String(obj[key] || '');
			});
			return result;
		};

		const normalizedLast = normalizeObject(lastIndexedValue);
		const normalizedSecondLast = normalizeObject(secondLastIndexedValue);

		// Compare key fields to determine if there are changes
		const keysToCompare = [
			'paymentFrequency',
			'paymentTime',
			'inputChangeLeaseTerm',
			'inputChangeGrossAmount',
			'inputChangeDiscountRate',
			'inputChangeEscalation',
		];

		const hasChanged = keysToCompare.some((key) => normalizedLast[key] !== normalizedSecondLast[key]);

		setLeaseHasChange(hasChanged);
	}, [changes]);

	useEffect(() => {
		setNewRowAdded(leaseHasChange);
	}, [leaseHasChange]);

	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Action completed ', toastConfig);
			if (state?.processCode) {
				const queryParamData = encodeQueryParamData({ processCode: state?.processCode });
				navigate(`/case-identity/${queryParamData}`, { state: { processCode: state?.codeProcess } });
			} else {
				navigate('/case-identity', { state: { processCode: state?.codeProcess } });
			}
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, isUpdateError, navigate, state, updateError]);

	useEffect(() => {
		if (isEmpty(state?.processCode)) {
			navigate(-1);
		}
	}, [state?.processCode, navigate]);

	useEffect(() => {
		if (state?.processCode && state._id) {
			viewSingleIdentity({
				suffixCode: '',
				_id: state._id,
				operation: 'get-single',
				processCode: state?.processCode,
			});
		}
	}, [state?._id, state?.processCode, viewSingleIdentity]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				await queryV3Loader();
				toast.success('Success! Sent back to ready for processing. ', toastConfig);
			} catch (error) {
				console.error('Error in queryV3Loader:', error);
			}
		};

		if (isActionRequiredButtonClicked) {
			fetchData();
		}
	}, [flag, isActionRequiredButtonClicked, queryV3Loader]);

	useEffect(() => {
		if (!isPopulateClick) {
			queryV3Loader();
		}
	}, [form.getFieldValue('version'), companyCode, isPopulateClick, queryV3Loader]);

	useEffect(() => {
		if (statusData && statusData.leaseId) {
			handleShowAll();
		}
	}, [statusData, handleShowAll]);

	return (
		<Box>
			<DetailPageHeader image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />} title={'Edit Lease Record'} />

			<Spin spinning={isViewLoading} style={{ maxHeight: '100%', marginTop: '20px' }}>
				<Box
					sx={{
						p: 2,
						borderRadius: '4px',
						mt: 2,
						display: 'flex',
						flexDirection: 'column',
						gap: '20px',
						backgroundColor: '#ffffff',
					}}
				>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						onValuesChange={
							// onFormValuesChange
							() => {
								const UpdateCheck = updateButtonDisablilityCheck();
								const LoadCheck = isLoadNewDataButtonDisabled();
								setIsUpdateButtonDisabled(UpdateCheck);
								setIsLoadDataButtonDisabled(LoadCheck);
							}
						}
					>
						<AntdValuePreview form={form} show={true} />
						<CardAccordion
							title="General Lease Information"
							darkTitle={true}
							sx={{
								mb: 2,
							}}
							customHeaderSX={{
								backgroundColor: '#e1f0fb !important',
								p: '10px',
								fontSize: '16px !important',
							}}
							typographySX={{ fontSize: '16px !important' }}
						>
							<GeneralLeaseInformation screen="EDIT" constantFormValues={constantFormValues} statusData={statusData} />
						</CardAccordion>

						<CardAccordion
							title="Financial Terms & Conditions"
							customHeaderSX={{
								backgroundColor: '#e1f0fb !important',
								p: '10px',
							}}
							typographySX={{ fontSize: '16px !important' }}
							darkTitle={true}
							sx={{ mb: 2 }}
						>
							<FinancialTermsConditions
								screen="EDIT"
								constantFormValues={constantFormValues}
								handleLeaseDateChange={debouncedHandleLeaseDateChange}
								form={form}
								LeaseEndDate={dayjs(statusData?.leaseEndDate)}
							/>
						</CardAccordion>

						<CardAccordion
							title="Accounting Information"
							customHeaderSX={{
								backgroundColor: '#e1f0fb !important',
								p: '10px',
							}}
							typographySX={{ fontSize: '16px !important' }}
							darkTitle={true}
							sx={{ mb: 2 }}
						>
							<AccountingInformation
								screen="EDIT"
								pdfFileList={pdfFileList}
								setPdfFileList={setPdfFileList}
								constantFormValues={constantFormValues}
								lynFormProperties={lynFormProperties}
								form={form}
							/>
						</CardAccordion>

						<CardAccordion
							title="Modification - IFRS 16-Specific Fields"
							customHeaderSX={{
								fontSize: '16px !important',
								backgroundColor: '#e1f0fb !important',
								p: '10px',
							}}
							typographySX={{ fontSize: '16px !important' }}
							darkTitle={true}
						>
							<IFRS16FieldsModifications
								screen="EDIT"
								constantFormValues={constantFormValues}
								handleInputMonthChange={handleInputMonthChange}
								form={form}
								rowIndex={rowIndex}
							/>

							<ActionButtons
								screen="EDIT"
								handleAddRow={handleAddRow}
								handlePreview={handlePreview}
								handleUpdateButtonActions={handleUpdateButtonActions}
								isLoadDataButtonDisabled={isLoadDataButtonDisabled}
								newRowAdded={newRowAdded}
								isUpdateButtonDisabled={isUpdateButtonDisabled}
								isScheduleLoading={isSendControlDataLoading}
								viewData={viewData}
								state={state}
								navigate={navigate}
							/>
						</CardAccordion>
					</Form>
					<CardAccordion
						title="Lease Schedule"
						customHeaderSX={{
							fontSize: '16px !important',
							backgroundColor: '#e1f0fb !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
					>
						{isViewSuccess && (
							<Spin spinning={isLeaseTableDataLoading || isSendControlDataLoading}>
								{leaseControlData?.length > 0 && (
									<LeaseTable
										leaseResponseData={leaseControlData ?? []}
										viewData={viewData}
										initialFormValues={initialFormValues}
										statusData={statusData}
										identityData={initialFormValues}
										screen={'EDIT'}
										form={form}
										setFlag={setFlag}
										setIsActionRequiredButtonClicked={setIsActionRequiredButtonClicked}
									/>
								)}
							</Spin>
						)}
					</CardAccordion>
					<CardAccordion
						title="Year Liability Data"
						customHeaderSX={{
							fontSize: '16px !important',
							backgroundColor: '#e1f0fb !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
					>
						<Row gutter={[16, 0]}>
							<Col xs={4}>
								<InputLabel label="Current Year Liability" head={true} />
							</Col>
							<Col xs={8}>
								<NumericFormat
									disabled
									size="large"
									value={yearLiabilityData?.currentYearLiability}
									thousandSeparator={true}
									// prefix={'$'}
									decimalScale={2}
									fixedDecimalScale={true}
									displayType={'input'}
									readOnly={true}
									customInput={Input} // Replace with your Input component
									style={{ textAlign: 'right', height: '32px' }}
								/>
							</Col>

							<Col xs={4}>
								<InputLabel label="Next Year Liability" head={true} />
							</Col>
							<Col xs={8}>
								<NumericFormat
									disabled
									size="large"
									value={yearLiabilityData?.nextYearLiability}
									thousandSeparator={true}
									// prefix={'$'}
									decimalScale={2}
									fixedDecimalScale={true}
									displayType={'input'}
									readOnly={true}
									customInput={Input} // Replace with your Input component
									style={{ textAlign: 'right', height: '32px' }}
								/>
							</Col>

							<Col xs={4}>
								<InputLabel label="Current Year Remaining Liability" head={true} />
							</Col>
							<Col xs={8}>
								<NumericFormat
									disabled
									size="large"
									value={yearLiabilityData?.currentYearRemainingLiability}
									thousandSeparator={true}
									// prefix={'$'}
									decimalScale={2}
									fixedDecimalScale={true}
									displayType={'input'}
									readOnly={true}
									customInput={Input} // Replace with your Input component
									style={{ textAlign: 'right', height: '32px' }}
								/>
							</Col>
						</Row>
					</CardAccordion>
					{showAllDataList?.responseData?.length > 0 ? (
						<Spin
							spinning={showAllDataList?.responseData?.length <= 0 && showAllDataList?.status !== 'SUCCESS'}
							style={{ maxHeight: '100%' }}
						>
							<CardAccordion
								title="Total Lease View"
								darkTitle={true}
								sx={{
									mt: 2,
									mb: 2,
								}}
								customHeaderSX={{
									backgroundColor: '#e1f0fb !important',
									p: '10px',
									fontSize: '16px !important',
								}}
								typographySX={{ fontSize: '16px !important' }}
							>
								<LeaseDetailsShowAll responseData={showAllDataList?.responseData} isLoading={isSendControlDataLoading} />
							</CardAccordion>
						</Spin>
					) : (
						<></>
					)}
				</Box>
			</Spin>

			{showModal && (
				<ComptrollerConfirmModal
					showModal={showModal}
					modalData={modalData}
					setShowModal={setShowModal}
					handleSubmit={() =>
						UpdateSingleIdentity({
							operation: 'update-single',
							processCode: state?.processCode,
							suffixCode: '',
							data: submitData,
							_id: state._id,
							version: +viewData?.version,
						})
					}
				/>
			)}
		</Box>
	);
};

export default Edit;
